import { isPlatformBrowser } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit, Optional, PLATFORM_ID, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subject, catchError, map, of, take, takeUntil } from "rxjs";
import { CommonVariable } from "src/app/app.constants";
import { StoreRepositoryService } from "src/app/pages/store/store-repository.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { FormatJackpot } from "../../pipe/formatJackpot";

@Component({
  selector: "app-checkout-confirmation",
  templateUrl: "./checkout-confirmation.component.html",
  styleUrls: ["./checkout-confirmation.component.scss"],
  standalone: true,
  imports: [TranslatePipe, FormatJackpot],
})
export class CheckoutConfirmationComponent implements OnInit, OnDestroy {
  isDiscountLottery = false;
  preferredCurrency: string = CommonVariable.preferredCurrency;
  preferredCurrencySignal = signal<string>(CommonVariable.preferredCurrency);
  currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  currencySymbolSignal = signal<string>(CommonVariable.defaultCurrencySymbol);
  selectedLanguage: string = CommonVariable.preferredLanguage;
  selectedLanguageSignal = signal<string>(CommonVariable.preferredLanguage);
  lottery: Data;
  private unsubscribe$ = new Subject<void>();
  public prizeValues = [];
  public fadeIn: boolean;
  public fadeOut: boolean;
  constructor(
    private storeService: StoreRepositoryService,
    private authService: AuthService,
    public translate: TranslateService,
    @Optional() private gtmSegmentService: GTMSegmentService,
    private matDialogRef: MatDialogRef<CheckoutConfirmationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Data,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.gtmSegmentService?.trackEvent("leaveCheckoutPopup");
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.currencySymbol = CommonVariable.defaultCurrencySymbol;
    this.fadeIn = false;
    this.fadeOut = false;
    this.lottery = this.readLotteryFromSession();
    this.authService.selectedlanguage$?.pipe(takeUntil(this.unsubscribe$))?.subscribe({
      next: (l) => {
        if (l) {
          this.selectedLanguageSignal.set(l ? l : CommonVariable?.preferredLanguage);
          this.selectedLanguage = this.selectedLanguageSignal();
          translate.use(l ? l : CommonVariable?.preferredLanguage);
        }
      },
      error: (err) => {
        // Handle the error appropriately
        return err;
      },
    });

    this.authService?.userDetails$?.pipe(takeUntil(this.unsubscribe$))?.subscribe({
      next: (user) => {
        this.preferredCurrencySignal.set(user?.preferred_currency ?? CommonVariable.preferredCurrency);
        this.preferredCurrency = this.preferredCurrencySignal();
        this.authService.getCurrencyList$?.pipe(takeUntil(this.unsubscribe$))?.subscribe({
          next: (data: Data) => {
            const CurrencyDetails = data?.details?.find((x) => x?.currency_code === this.preferredCurrencySignal());
            this.currencySymbolSignal.set(CurrencyDetails?.currency_symbol ?? CommonVariable.defaultCurrencySymbol);
            this.currencySymbol = this.currencySymbolSignal();
          },
          error: (error) => {
            return error;
          },
        });
      },
      error: (err) => {
        // Handle the error appropriately
        return err;
      },
    });

    if (this.lottery && this.lottery?.page == "offerOfDay") {
      this.isDiscountLottery = true;
    } else {
      this.isDiscountLottery = false;
      this.storeService.selectedOfferDataPage$
        .pipe(
          take(1),
          map((res) => res?.lottery),
          catchError(() => of(null)),
        )
        .subscribe({
          next: (value) => {
            this.lottery = this.readLotteryFromSession() ?? value;
          },
          error: (error) => {
            return error;
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    const fadeInTimeout = 2000;
    const fadeOutTimeout = 4000;
    // This method is used to display the dialog box after 2 seconds
    setTimeout(() => {
      this.fadeIn = true;
    }, fadeInTimeout);
    // This method is used to display the dialog box after 4 seconds
    setTimeout(() => {
      this.fadeOut = true;
    }, fadeOutTimeout);
  }

  close() {
    this.matDialogRef.close(false);
  }

  backToPreviousPage() {
    this.matDialogRef.close(true);
  }

  /*
      Why this is better:
      Guards SSR with isPlatformBrowser (no sessionStorage on server).
      Encapsulates storage parsing in a helper → safer, testable, reusable.
      Keeps jackpotValue as a derived property → no duplicated state.
      Still allows session cache but prefers fresh API data later.
      */
  private readLotteryFromSession(): Data | undefined {
    if (!isPlatformBrowser(this.platformId)) return undefined; // SSR guard

    try {
      const raw = sessionStorage.getItem("recentLotteryDetails");
      return raw ? JSON.parse(raw) as Data : undefined;
    } catch {
      return undefined;
    }
  }
  get jackpotValue(): number | string | undefined {
    const jackpot = this.lottery?.jackpot_value;
    if (jackpot !== undefined && jackpot !== '') {
      return jackpot;
    }
    if (isPlatformBrowser(this.platformId)) {
      try {
        const raw = localStorage.getItem('exit_intent_offer_meta');
        const stored = raw ? JSON.parse(raw) : null;
        const price = stored?.price;
        return price ?? undefined;
      } catch (err) {
        return undefined;
      }
    }
    return undefined;
  }

}
